// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import type { TriageSortState } from '@polarissleep/shared';
import { IconChevronLeft, IconChevronRight, IconInfoCircle, IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import type { TreatmentMaintenanceSort } from '../../hooks/useTreatmentMaintenanceQueue';
import { RemoteMonitoringTable } from './RemoteMonitoringTable';
import { TreatmentMaintenanceTable } from './TreatmentMaintenanceTable';
import styles from './TriagePage.module.css';

interface TreatmentMaintenanceStageCardProps {
  pageSize: number;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  hideTestPatients: boolean;
  setHideTestPatients: (value: boolean) => void;
  sortState: TriageSortState | null;
  onSortChange: (next: TriageSortState | null) => void;
}

export function TreatmentMaintenanceStageCard({
  pageSize,
  searchInput,
  onSearchInputChange,
  hideTestPatients,
  setHideTestPatients,
  sortState,
  onSortChange,
}: TreatmentMaintenanceStageCardProps): JSX.Element {
  const [page, setPage] = useState(1);
  const [rpmPage, setRpmPage] = useState(1);
  const [maintenanceSort, setMaintenanceSort] = useState<TreatmentMaintenanceSort>('asc');
  const [total, setTotal] = useState(0);
  const [rpmTotal, setRpmTotal] = useState(0);
  return (
    <Paper className={`${styles.section} ${styles.maintenanceSection}`} withBorder radius="md" shadow="sm" p="lg">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group gap="sm" align="center">
            <Title order={2}>Treatment maintenance</Title>
            {total > 0 && (
              <Badge color="gray" variant="light" radius="xl">
                {total}
              </Badge>
            )}
          </Group>
          <Group gap="md" align="center">
            <Tooltip label="Hide test patients" withArrow openDelay={0}>
              <span style={{ display: 'inline-flex' }}>
                <Switch
                  aria-label="Hide test patients"
                  size="sm"
                  checked={hideTestPatients}
                  onChange={(event) => setHideTestPatients(event.currentTarget.checked)}
                />
              </span>
            </Tooltip>
            <TextInput
              placeholder="Search"
              leftSection={<IconSearch size={14} />}
              rightSection={searchInput ? <Loader size={14} /> : undefined}
              size="sm"
              w={240}
              value={searchInput}
              onChange={(event) => {
                setPage(1);
                onSearchInputChange(event.currentTarget.value);
              }}
            />
          </Group>
        </Group>
        <TreatmentMaintenanceTable
          page={page}
          pageSize={pageSize}
          search={searchInput}
          onPageChange={setPage}
          onTotalChange={setTotal}
          hideTestPatients={hideTestPatients}
          sort={maintenanceSort}
          onSortChange={setMaintenanceSort}
        />
        <Divider />
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="sm">
            <Title order={4}>Remote Patient Monitoring</Title>
            <Badge color="polaris" variant="light" radius="xl">
              {rpmTotal} active
            </Badge>
          </Group>
          <Group gap={6} c="dimmed">
            <IconInfoCircle size={14} />
            <Text size="sm">Manual entry · click a patient for contact info</Text>
          </Group>
        </Group>
        <Paper withBorder radius="sm" p="sm" bg="gray.0">
          <Group align="flex-start" gap="sm" wrap="nowrap">
            <IconInfoCircle size={16} color="#640040" />
            <Text size="sm" c="dimmed">
              Lists every patient with <b>RPM status = Active</b> — auto-enrolled when their triage status is a
              follow-up (30 / 60 / 90 / 180-day, Misc. or RPM), or set manually on the <b>patient profile</b>.
            </Text>
          </Group>
        </Paper>
        <RemoteMonitoringTable
          page={rpmPage}
          pageSize={pageSize}
          search={searchInput}
          onPageChange={setRpmPage}
          sortState={sortState}
          onSortChange={onSortChange}
          onTotalChange={setRpmTotal}
          hideTestPatients={hideTestPatients}
        />
        <Group justify="space-between" align="center" px="xs" pt={4}>
          <Text size="sm" c="dimmed">
            {total + rpmTotal === 0 ? '0 of 0' : `1 - ${Math.min(pageSize, total + rpmTotal)} of ${total + rpmTotal}`}
          </Text>
          <Group gap={6}>
            <Button
              variant="default"
              size="compact-sm"
              aria-label="Previous page"
              disabled={page <= 1 && rpmPage <= 1}
              onClick={() => {
                setPage((current) => Math.max(1, current - 1));
                setRpmPage((current) => Math.max(1, current - 1));
              }}
            >
              <IconChevronLeft size={16} />
            </Button>
            <Button variant="filled" color="polaris" size="compact-sm" aria-current="page">
              {page}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              aria-label="Next page"
              disabled={pageSize * page >= total && pageSize * rpmPage >= rpmTotal}
              onClick={() => {
                setPage((current) => current + 1);
                setRpmPage((current) => current + 1);
              }}
            >
              <IconChevronRight size={16} />
            </Button>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}
